import { useContext, useEffect } from 'react';

import { MATCHING } from '@common/constants';
import { DrawingShape } from '@common/models';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedEdge } from '@ee-api/edges-api/hooks/useSelectedEdge';
import { useEdges } from '@ee-api/edges-api/useEdges';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch } from '@redux/hooks';
import { setNeedRedrawCanvas } from '@redux/slices/drawingShapeSlice';

import drawingTool from '../../../pages/easy-estimator/components/scrollable-area/design-countertop/drawer/drawingTool';

type UseEquivalentMatchingReturn = undefined;

/**
 * useEquivalentMatchingDisabled fixes edges on shapes
 * when the product catalog changes (really just the edges matter, but any change will do it)
 */
const useEquivalentMatchingDisabled = (): UseEquivalentMatchingReturn => {
    const { drawingShapes, updateActiveDrawingShape, lastActiveDrawingShape, updateDrawingShapes } =
        useContext(DrawingShapeContext);

    const { sortedEdges: edges } = useEdges();
    const { selectedValue: selectedEdge } = useSelectedEdge();
    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);
    const { retailerSettings } = useRetailerSettings();

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (
            retailerSettings?.enableMatching !== MATCHING.DISABLE || // this is the most significant item
            !areThereShapesInTheDrawingArea ||
            !edges.length
        ) {
            return;
        }

        // fix edges then return - they were updating previously and could change with thickness change (ugh!)
        const clonedDrawingShapes = drawingTool.cloneShapes(drawingShapes);
        let isUpdated = false;
        clonedDrawingShapes.forEach((drawingShape: DrawingShape) => {
            if (
                (drawingShape.edge === undefined && selectedEdge !== undefined) ||
                (selectedEdge && !edges.find((edge) => drawingShape.edge?.productId === edge.productId))
            ) {
                isUpdated = true;
                drawingShape.edge = selectedEdge;
            }
        });
        if (isUpdated) {
            updateDrawingShapes(clonedDrawingShapes); // only update the shapes if things changed
            updateActiveDrawingShape(lastActiveDrawingShape);
            dispatch(setNeedRedrawCanvas(true));
        }
    }, [
        areThereShapesInTheDrawingArea,
        dispatch,
        drawingShapes,
        edges,
        lastActiveDrawingShape,
        retailerSettings?.enableMatching,
        selectedEdge,
        updateActiveDrawingShape,
        updateDrawingShapes,
    ]);

    return undefined;
};
export default useEquivalentMatchingDisabled;
